import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { RideStatusType } from '../../shared/enums/rideStatusType';
import moment from 'moment';

export const invalidFormValidation = (form: FormGroup) => {
  Object.values(form.controls).forEach(control => {
    control.invalid && control.setErrors({ ...control.errors, invalid: true });
  });
};
 

export const formatUnixTimestamp = (timestamp: number, format: string = 'h:mm A'): string => {
  return moment.unix(timestamp).local().format(format);
};

export const changeTime = (datetime: string, format: string, timezoneOffsetInSeconds: number) => {
  let timeToReturn;
  const timezoneDropdownValue: string = localStorage.getItem('selectedTimezone') ?? '';
  const serverTimezone: string = localStorage.getItem('serverTimezone') ?? '';
  if (timezoneDropdownValue == 'systemPstTimezone') {
    timeToReturn = moment(datetime).utc().zone(serverTimezone).format(format);
  }else if (timezoneDropdownValue == 'RideTimezone'){
      let strOffset = '';
      const timeOffset = timezoneOffsetInSeconds / 60 / 60;
      if (timeOffset < 0) {
        strOffset = `-0${timeOffset * -1}:00`;
      } else {
        strOffset = `0${timeOffset * -1}:00`;
      }

     timeToReturn = moment(datetime).utc().zone(strOffset).format(format);
  } else {
    timeToReturn = moment(datetime).local().format(format);
  }
  return timeToReturn;
};

export const getLanguage = (abbreviation: string): string => {
  const languageMap: { [key: string]: string } = {
    en: 'English',
    ar: 'Arabic',
    fr: 'France',
    bn: 'Bengali',
    cantonese: 'Cantonese',
    de: 'German',
    el: 'Greek',
    hi: 'Hindi',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    pa: 'Punjabi',
    pt: 'Portuguese',
    mandarin: 'Mandarin',
    es: 'Spanish',
    ru: 'Russian',
    th: 'Thai',
    tr: 'Turkish',
    vi: 'Vietnamese',
    Other: 'Other',
  };

  // Return the language name if the abbreviation exists, otherwise return "Other"
  return languageMap[abbreviation] || 'Other';
};

export const getFormattedDate = (date: Date) => {
  const formattedDate = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  return formattedDate;
}

/**
 * Custom validator to check if the string is not just whitespace
 */
export const noWhitespaceValidator = (control: AbstractControl): ValidationErrors | null => {
  if (control.value && typeof control.value === 'string' && control.value.trim().length === 0) {
    return { whitespace: true };
  }
  return null;
};

/**
 * Custom validator to check if the selected driver has a camera when the ride requires it
 * @param isCameraRequired - Whether the ride requires a camera
 * @returns Validator function
 */
export const requireCameraValidator = (isCameraRequired: boolean) => {
  return (control: AbstractControl): ValidationErrors | null => {
    const driver = control.value;
    
    if (!driver) {
      return null; // Let required validator handle empty state
    }
    
    // Only validate if the ride requires a camera
    if (isCameraRequired && !driver.cameraStatus?.camera_id) {
      return { requiresCamera: true };
    }
    
    return null;
  };
};

/**
 * Determines if the camera dialog should be enabled based on ride status and time
 * @param ride - The ride with timeline and info
 * @param rideStatusId - The current ride status ID
 * @returns boolean indicating if the camera dialog should be enabled
 */
export const isCameraDialogEnabled = (ride: any, rideStatusId: number): boolean => {
  if (!ride?.rideTimeline?.length || !ride?.rideInfo) {
    return true; // Default to enabled if no timeline data
  }

  // Always allow if ride is in progress or completed
  if (rideStatusId === RideStatusType.InProgress || rideStatusId === RideStatusType.Completed) {
    return true;
  }

  // Get current time in UTC
  const now = moment.utc();
  
  // Get the ride's first stop time (start of ride) - prefer actual over scheduled
  const dateOfService = getDateOfServiceInYYYYMMDD(ride.rideInfo.dateOfService || ride.rideInfo.strDateOfService);
  const firstStop = ride.rideTimeline[0];
  const firstStopTimeUtc = (firstStop.actualArrivalTimeUtc && firstStop.actualArrivalTimeUtc !== '00:00:00') 
    ? firstStop.actualArrivalTimeUtc 
    : firstStop.scheduled_arrival_time_utc;
  const firstStopTime = moment.utc(`${dateOfService} ${firstStopTimeUtc}`, 'YYYY-MM-DD HH:mm:ss');
  
  // Enable dialog if current time is same or after the first stop time
  return now.isSameOrAfter(firstStopTime);
};

/**
 * Extracts date of service from ride data (can be string or Firestore timestamp)
 * @param dateOfService - Date of service in string format (MM/DD/YYYY or YYYY-MM-DD) or Firestore timestamp
 * @returns Date string in YYYY-MM-DD format
 */
export const getDateOfServiceInYYYYMMDD = (dateOfService: any): string => {
  if (typeof dateOfService === 'string') {
    // Handle both MM/DD/YYYY and YYYY-MM-DD formats, parse in UTC
    return moment.utc(dateOfService, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
  } else if (dateOfService && typeof dateOfService === 'object' && 'seconds' in dateOfService) {
    // Convert Firestore timestamp to UTC moment
    return moment.utc(dateOfService.seconds * 1000).format('YYYY-MM-DD');
  }
  return moment.utc().format('YYYY-MM-DD');
};
